// ── <EngagementTracker /> — side-effect-only component.
// Mounted ONCE near the top of the app tree. Installs the engagement
// heartbeat + session-quality flush + returning_visit event handlers
// from analytics.js. Also wires SPA route changes to `noteActive()`
// so heartbeats keep firing when the child navigates between routes
// (previous audit showed GA4 wasn't seeing engagement inside games
// and canvas views because SPA route changes don't refire page_view).
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { installEngagementHeartbeat, noteActive, beginActivity, endActivity } from "@/lib/analytics";

// Routes that are "activities" for the 60s+ engagement threshold.
// Any prefix match counts — order doesn't matter.
const ACTIVITY_PREFIXES = [
  "/games",
  "/first-quest",
  "/quiz",
  "/coloring",
  "/globe",
  "/kaleidoscope",
  "/pixel-art",
  "/stained-glass",
  "/mandala",
  "/dodge",
  "/squishy",
  "/snap",
  "/spelling-bee",
  "/spell",
  "/puzzles",
  "/name-place-animal",
  "/treasure-quest",
  "/code-breaker",
  "/academy",
  "/learn-topics",
  "/lesson",
];

function routeToActivityId(pathname) {
  const match = ACTIVITY_PREFIXES.find((p) => pathname === p || pathname.startsWith(p + "/"));
  return match ? pathname.replace(/^\//, "").replace(/\//g, ":") : null;
}

export default function EngagementTracker() {
  const loc = useLocation();

  useEffect(() => {
    installEngagementHeartbeat();
    // Cycle 63 · Part 14 — pwa_launch event. Fires once per standalone
    // open so we can compare installed vs non-installed return rates
    // (the key measurement in the PWA retention command).
    try {
      const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator?.standalone === true
        || new URLSearchParams(window.location.search).get("source") === "pwa";
      if (isStandalone) {
        const K = "we_pwa_launch_seen_v1";
        const today = new Date().toISOString().slice(0, 10);
        if (localStorage.getItem(K) !== today) {
          localStorage.setItem(K, today);
          import("@/lib/analytics").then(({ trackEvent }) =>
            trackEvent("pwa_launch", { source: "standalone" })
          );
        }
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    noteActive();
    const actId = routeToActivityId(loc.pathname);
    if (actId) {
      beginActivity(actId);
    } else {
      endActivity();
    }
  }, [loc.pathname]);

  return null;
}
